import { useBehavior, useList } from '@libs/hooks';
import { TItem, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, useCallback, useEffect, useRef, useState } from 'react';
import { TCarouselData, TConsumerItem } from '../../types/data';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import styled from './carousel.module.css';
import styledNew from './carouseNew.module.css';
import { sanitizeUrl } from '../../utils/sanitize';

type TCursorPosition = {
    xStart: number;
    yStart: number;
};
const percent = 0.01;

export const Carousel = ({ widget }: TWidgetProps<TCarouselData>) => {
    const { handleClick } = useList(widget);
    const { isHidden } = useBehavior(widget);

    const cursorPosition = useRef({ xStart: 0, yStart: 0 } as TCursorPosition);
    const [slidesToShow, setSlidesToShow] = useState<number>(1);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [startX, setStartX] = useState<number>(0);
    const [drag, setDrag] = useState<number>(0);
    const data = widget.data ?? {};
    const slides = data.items ?? [];

    const autoPlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const sliderRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const updateSlidesToShow = () => {
            const width = window.innerWidth;
            if (slides.length > 2) {
                if (width >= 708) setSlidesToShow(3);
                else if (width >= 320) setSlidesToShow(2);
                else setSlidesToShow(1);
            } else if (slides.length === 2) {
                setSlidesToShow(2);
            } else {
                setSlidesToShow(1);
            }
        };
        updateSlidesToShow();
        window.addEventListener('resize', updateSlidesToShow);
        return () => window.removeEventListener('resize', updateSlidesToShow);
    }, [slides.length]);

    const displayIndex = Math.min(currentIndex, Math.max(0, slides.length - slidesToShow));

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex >= slides.length - slidesToShow ? 0 : prevIndex + 1));
    }, [slides.length, slidesToShow]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? Math.max(0, slides.length - slidesToShow) : prevIndex - 1));
    }, [slides.length, slidesToShow]);

    const goToSlide = (slideIndex: number) => {
        const newIndex = Math.min(slideIndex, Math.max(0, slides.length - slidesToShow));
        setCurrentIndex(newIndex);
    };

    const handleDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault();
        if ((e as React.MouseEvent<HTMLDivElement>)?.clientX) {
            cursorPosition.current.xStart = (e as React.MouseEvent<HTMLDivElement>).clientX;
            cursorPosition.current.yStart = (e as React.MouseEvent<HTMLDivElement>).clientY;
        }
        const startPosition = 'touches' in e ? e.touches[0].clientX : e.clientX;
        setIsDragging(true);
        setStartX(startPosition);
        if (autoPlayTimeoutRef.current) clearTimeout(autoPlayTimeoutRef.current);
        if (sliderRef.current) sliderRef.current.style.transition = 'none';
    };

    const handleDragMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        setDrag(currentX - startX);
    };

    const handleDragEnd = () => {
        if (!isDragging) return;
        if (sliderRef.current) sliderRef.current.style.transition = 'transform 0.5s ease-in-out';
        setIsDragging(false);
        const dragThreshold = 50;
        if (drag < -dragThreshold) goToNext();
        else if (drag > dragThreshold) goToPrev();
        setDrag(0);
    };

    const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: TItem) => {
        const deltaX = Math.abs(e.clientX - cursorPosition.current.xStart);
        const deltaY = Math.abs(e.clientY - cursorPosition.current.yStart);
        if (deltaX < window.innerWidth * percent && deltaY < window.innerHeight * percent) {
            handleClick(widget?.uid, item);
        }
    };

    const Slide: React.FC<{ item: TConsumerItem }> = ({ item }) => (
        <div
            className={clsx(
                styled.buttonStory,
                item.imageSource && styled.imageSource,
                !item.imageSource && item.background && styled[`${item.background}`],
                !item.imageSource && !item.background && styled.blue_1,
            )}
            style={getInlineStyles({ variables: { imageSource: `url(${sanitizeUrl(item.imageSource)})` } })}
            // onMouseDown={(e) => onMouseDown(e)}
            onMouseUp={(e) => onMouseUp(e, item)}
        >
            <TextCommon isWidget={false} content={item.text} options={{ variant: 'secondary', textColor: 'white_base' }} />
        </div>
    );

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div
                data-test={widget.uid}
                className={styledNew.carouselContainer}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
                style={getInlineStyles({ margin: widget.options?.margin })}
            >
                <div
                    ref={sliderRef}
                    className={styledNew.sliderTrack}
                    style={{ transform: `translateX(calc(-${displayIndex * (100 / slidesToShow)}% + ${drag}px))` }}
                >
                    {slides.map((slide) => (
                        <div key={'slide_' + slide.id} className={styledNew.slideWrapper} style={{ width: `${100 / slidesToShow}%` }}>
                            <Slide item={slide} />
                        </div>
                    ))}
                </div>
                <div className={styled.carouselDotGroup}>
                    <div className={styledNew.dotGroup}>
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToSlide(index);
                                }}
                                aria-label={`Go to slide ${index + 1}`}
                                disabled={index >= displayIndex && index < displayIndex + slidesToShow}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </Activity>
    );
};
